import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { SignalRGroup } from "src/components";
import { useSession, useSignalR } from "src/hooks";
import { configuration } from "../../../configuration";
import { FetchError, getActiveMeetingNotificationsForUserDto } from "../../types";
import { useAccessToken } from "../../use-access-token";
import { queryKeys } from "../queryKeys";

const useGetActiveMeetingsForUserRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function getActiveMeetingsForUserRequest(): Promise<getActiveMeetingNotificationsForUserDto> {
    const accessToken = await getAccessToken();
    const response = await fetch(`${configuration.apiRootUrl}/me/meetings`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new FetchError(response);
    }

    return response.json();
  }

  return { getActiveMeetingsForUserRequest };
};

const useGetActiveMeetingsForUser = () => {
  const { getActiveMeetingsForUserRequest } = useGetActiveMeetingsForUserRequest();
  const { userId } = useSession();
  const signalR = useSignalR();
  const queryClient = useQueryClient();

  useEffect(() => {
    signalR.joinGroup(SignalRGroup.MeetingNotificationsForUser(userId));
    return () => signalR.leaveGroup(SignalRGroup.MeetingNotificationsForUser(userId));
  }, [signalR, userId]);

  useEffect(() => {
    const handleRefetchMeetings = () => {
      queryClient.invalidateQueries(queryKeys.meetings.getActiveMeetingsForUser());
    };

    signalR.onRefetchActiveMeetingNotifications(handleRefetchMeetings);
    return () => signalR.offRefetchActiveMeetingNotifications(handleRefetchMeetings);
  }, [signalR, queryClient]);

  return useQuery({
    queryKey: queryKeys.meetings.getActiveMeetingsForUser(),
    queryFn: getActiveMeetingsForUserRequest,
  });
};

export { useGetActiveMeetingsForUser };
